import { useToast } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import React from 'react';
import { ConnectionHandler } from 'utils/graphClient';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { useAuthenticatedSession } from 'utils/sessionProvider';
import { notEmpty } from 'utils/NotEmptyFilter';
import { EmojiPicker } from '../../../../../../../../components/ChatApp/components/ChatMenu/components/ChatThread/components/EmojiPicker';
import { ReactionButton_chat_createSocialThreadReaction_Mutation } from './__generated__/ReactionButton_chat_createSocialThreadReaction_Mutation.graphql';
import { ReactionButton_chat_socialThreadFragment$key } from './__generated__/ReactionButton_chat_socialThreadFragment.graphql';

const socialThreadFragment = graphql`
  fragment ReactionButton_chat_socialThreadFragment on SocialThread {
    id
    reactions(first: 1000) @connection(key: "CommentsThread_reactions") {
      edges {
        node {
          id
          text
          senderId
          deletedAt
        }
      }
    }
  }
`;

const createReactionMutation = graphql`
  mutation ReactionButton_chat_createSocialThreadReaction_Mutation(
    $input: CreateThreadReactionInput!
  ) {
    createThreadReaction(input: $input) {
      id
      text
      senderId
      deletedAt
      conversation {
        id
      }
    }
  }
`;

interface ReactionButtonProps {
  socialThread: ReactionButton_chat_socialThreadFragment$key;
  announcementId: string;
}

export const ReactionButton: React.FC<ReactionButtonProps> = ({
  socialThread,
  announcementId,
}) => {
  const { id: socialThreadId, reactions } = useFragment(
    socialThreadFragment,
    socialThread,
  );
  const toast = useToast();
  const onErrorToast = ({ action }: { action: 'add' | 'remove' }) =>
    toast({
      status: 'error',
      position: 'top-right',
      title:
        action === 'add'
          ? t`Could not add your reaction`
          : t`Could not remove that reaction`,
    });

  const [createReaction] =
    useMutation<ReactionButton_chat_createSocialThreadReaction_Mutation>(
      createReactionMutation,
    );
  const { member: sessionMember } = useAuthenticatedSession();
  const reactionNodes = reactions.edges.map((e) => e.node);

  const handleEmojiPick = (emoji: string) => {
    const myReaction = reactionNodes.find(
      (n) =>
        !n.deletedAt && n.senderId === sessionMember.id && n.text === emoji,
    );

    if (myReaction) {
      // Already reacted this way? Great!
      return;
    }

    const updater = (store: any) => {
      const reaction = store.getRootField('createThreadReaction');
      const thread = store.get(socialThreadId);

      if (thread) {
        const reactions = ConnectionHandler.getConnection(
          thread,
          'CommentsThread_reactions',
        );

        if (reactions) {
          const edges = reactions.getLinkedRecords('edges');
          const reactionEdge = ConnectionHandler.createEdge(
            store,
            reactions,
            reaction,
            'ReactionEdge',
          );

          const newEdges = [...(edges || []), reactionEdge].filter(notEmpty);

          reactions.setLinkedRecords(newEdges, 'edges');
        }
      }
    };

    createReaction({
      variables: {
        input: {
          referenceId: announcementId,
          reaction: emoji,
        },
      },
      payloadErrorBehaviour: 'HANDLED',
      onError: (e) => {
        onErrorToast({ action: 'add' });
      },
      updater,
      optimisticResponse: {
        createThreadReaction: {
          id: `optimistic-${Date.now()}`,
          text: emoji,
          senderId: sessionMember.id,
          deletedAt: null,
          conversation: {
            id: socialThreadId,
          },
          reactions: {
            edges: [],
          },
        },
      },
      optimisticUpdater: updater,
    });
  };

  return <EmojiPicker onEmojiPick={handleEmojiPick} />;
};
